import React, { useEffect, useMemo, useRef, useState } from 'react';
import { openPicker } from '../../utils/timePicker';
import { BackPill } from './BackPill';
import axios from 'axios';
import {
  ChevronLeft, Plus, X, Trash2, Check, Play, Square, Eye,
  Heart, Sparkles, Clock, BookOpen, Wind,
} from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { pingAuraFuse } from '../../utils/auraPing';
import { pushNotesToCodex } from '../../utils/energySync';
import { igniteSfx, amplifySfx, manifestSfx } from '../../utils/ritualSfx';
import {
  startBreathSession, stopBreathSession, startChime, endChime, speak, stopSpeech,
} from '../../utils/divineSounds';

/* ═══════════════════════════════════════════════════════════════════
   THE DIVINE ENERGY — 10+10+10 Imperial Protocol
   Phase 1 (10m): Forgiveness + Gratitude (already have / will have)
   Phase 2 (10m): Divine Breath + Visualization (20 cycles × 30s)
   Phase 3 (10m): Worship (read note + mark done) + Decree/Ask
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_SOFT = '#8B6B2F';
const GOLD_BRIGHT = '#E9C77A';
const AMETHYST = '#8B3FBF';
const CRIMSON = '#B22222';
const AZURE = '#5DD4FF';
const BG = '#050308';

const PHASE_CFG = {
  1: { title: 'Calibration',   subtitle: 'Forgiveness · Gratitude',        color: CRIMSON },
  2: { title: 'Elevation',     subtitle: 'Breath · Visualization',         color: AZURE },
  3: { title: 'Communion',     subtitle: 'Worship · Decree',               color: AMETHYST },
};

const todayKey = () => new Date().toISOString().slice(0, 10);
const tzOffsetMin = () => -new Date().getTimezoneOffset();
const fmtTime = (s) => {
  const m = Math.floor(Math.max(0, s) / 60);
  const r = Math.max(0, s) % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

/* ── Header shared by all phase screens, with phase-skip nav pills ── */
const PhaseHeader = ({ phase, onBack, onJumpPhase, communionFlicker }) => (
  <div className="flex-shrink-0 border-b"
    style={{ borderColor: `${GOLD}22` }}>
    <div className="flex items-center justify-between px-3 py-2.5">
      <BackPill onClick={onBack} accent="#B978FF" testId="divine-back-btn"/>
      <div className="text-center">
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: `0 0 10px ${GOLD}55` }}>
          ENERGY · PHASE {phase}
        </p>
        <p className="font-['Outfit'] uppercase tracking-[0.28em] mt-0.5"
          style={{ color: PHASE_CFG[phase].color, fontSize: 8 }}>
          {PHASE_CFG[phase].title} · {PHASE_CFG[phase].subtitle}
        </p>
      </div>
      <span className="w-14"/>
    </div>
    {/* Phase-skip pill nav — user can jump to any phase */}
    <div className="flex items-center justify-center gap-1.5 pb-2 px-2" data-testid="divine-phase-nav">
      {[1, 2, 3].map(p => {
        const cfg = PHASE_CFG[p];
        const active = p === phase;
        const flicker = p === 3 && communionFlicker && !active;
        return (
          <button key={p} type="button"
            onClick={() => p !== phase && onJumpPhase(p)}
            data-testid={`divine-jump-phase-${p}`}
            className={`rounded-full px-3 py-1 font-['Outfit'] uppercase tracking-[0.18em] transition-all ${flicker ? 'divine-pill-flicker' : ''}`}
            style={{
              background: active
                ? `linear-gradient(180deg, ${cfg.color}, ${cfg.color}55)`
                : 'rgba(255,255,255,0.03)',
              color: active ? '#FFF' : `${cfg.color}dd`,
              border: `1px solid ${cfg.color}${active ? 'ff' : '44'}`,
              fontSize: 9,
              boxShadow: active ? `0 0 10px ${cfg.color}77` : 'none',
            }}>
            {p} · {cfg.title}
          </button>
        );
      })}
    </div>
    <style>{`
      @keyframes divinePillFlicker {
        0%, 100% { box-shadow: 0 0 4px rgba(139,63,191,0.4); border-color: rgba(139,63,191,0.6); }
        50%      { box-shadow: 0 0 16px rgba(255,246,208,0.85), 0 0 26px rgba(139,63,191,0.55); border-color: #FFF6D0; }
      }
      .divine-pill-flicker { animation: divinePillFlicker 1.4s ease-in-out infinite; }
    `}</style>
  </div>
);

/* ── Reusable list-of-strings editor (Forgiveness / Gratitude lines) ── */
const ListEditor = ({ items, onChange, placeholder, color, testidPrefix, max = 20 }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.length >= max) return;
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex gap-1.5 mb-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testidPrefix}-input`}
          className="flex-1 rounded-lg px-3 py-2 font-['Outfit']"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${color}55`,
            color: 'rgba(255,255,255,0.9)',
            fontSize: 12, outline: 'none',
          }}/>
        <button type="button" onClick={add}
          data-testid={`${testidPrefix}-add-btn`}
          className="rounded-lg px-3 flex items-center justify-center"
          style={{
            background: `linear-gradient(180deg, ${color}, ${color}77)`,
            border: `1px solid ${color}`,
            color: '#0a0a0a', minWidth: 44,
          }}>
          <Plus size={16} strokeWidth={2.5}/>
        </button>
      </div>
      <div className="space-y-1.5" data-testid={`${testidPrefix}-list`}>
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg px-3 py-2"
            style={{ background: `${color}12`, border: `1px solid ${color}44` }}>
            <span className="font-['Outfit'] flex-1"
              style={{ color: 'rgba(255,255,255,0.88)', fontSize: 12 }}>
              {it}
            </span>
            <button type="button" onClick={() => remove(i)}
              data-testid={`${testidPrefix}-remove-${i}`}
              className="rounded-full p-1 opacity-70 hover:opacity-100">
              <X size={14} style={{ color: color }}/>
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="font-['Outfit'] italic text-center py-2"
            style={{ color: `${color}88`, fontSize: 11 }}>
            (empty — add one and press Enter)
          </p>
        )}
      </div>
    </div>
  );
};

/* ═════════ PHASE 1 — 4 sub-tabs: Forgive Self / Forgive Others / Have / Will ═════════

   UX design (per user):
   - Big JOURNAL-STYLE textarea per tab (not chat-line)
   - User writes freely, then clicks "Release" (forgiveness) or "Attract" (gratitude)
   - The written sheet transforms into a GOLDEN ENERGY CARD with an affirmation
   - Data model: newline-split into the existing array field (forgiveness /
     forgiveness_others / gratitude_have / gratitude_will) so backend stays
     backwards compatible.
   ═══════════════════════════════════════════════════════════════════════ */

const P1_TABS = [
  {
    key: 'self', field: 'forgiveness', color: CRIMSON,
    label: 'Forgive Self', short: 'SELF',
    question: 'Are you ready to forgive yourself?',
    hint: 'Past mistakes · missed chances · lack of confidence · shyness · inferior complex. Write it out completely. Release it.',
    placeholder: 'I forgive myself for...\n\nI release the weight of...\n\nI let go of...',
    ctaLabel: 'Release · Ignite',
    affirmation: 'Every burden you released just became rocket fuel. Your energy field is now unbound.',
  },
  {
    key: 'others', field: 'forgiveness_others', color: '#D87979',
    label: 'Forgive Others', short: 'OTHERS',
    question: 'Are you ready to forgive others?',
    hint: 'Forgive others their actions. This is NOT for them — it is for your sovereignty. Free yourself.',
    placeholder: 'I forgive them for...\n\nTheir actions no longer own my energy...\n\nI cut the cord...',
    ctaLabel: 'Release · Ignite',
    affirmation: 'You just cut every invisible cord that drained you. What was theirs is no longer yours to carry.',
  },
  {
    key: 'have', field: 'gratitude_have', color: GOLD,
    label: 'Gratitude · Have', short: 'HAVE',
    question: 'Are you ready to attract more?',
    hint: 'Tell what you feel grateful to have in your life right now. Health, breath, allies, a bed, a mind that thinks. Speak them.',
    placeholder: 'I am grateful for...\n\nI honor...\n\nI recognize the gift of...',
    ctaLabel: 'Attract · Amplify',
    affirmation: 'Gratitude is a magnet. The Universe just heard your signal — expect more of the same, multiplied.',
  },
  {
    key: 'will', field: 'gratitude_will', color: AMETHYST,
    label: 'Gratitude · Will', short: 'WILL',
    question: 'Are you ready to receive?',
    hint: 'Gratitudes you already have — in the future. Write in PRESENT TENSE as if they are already yours. "I am..." / "I have..."',
    placeholder: 'I am the founder of...\n\nI have the body of...\n\nI live in the home of...',
    ctaLabel: 'Command · Manifest',
    affirmation: 'Reality just registered your decree. Every word you wrote is already on its way to you.',
  },
];

/* Journal sheet — big writing surface with LOCAL draft buffer.
   On Ignite: draft is committed to journal array (append), then cleared.
   On Edit-again: affirmation dismissed, draft is empty → clean textarea for next round.
   Committed items persist in the journal state (and to backend on phase-save),
   so the user's words are never lost — only the visual surface resets. */
const RELEASE_SFX = { self: igniteSfx, others: igniteSfx, have: amplifySfx, will: manifestSfx };

/* Draft + ignited state live in Phase1 so the footer can show the Release button while typing. */
const JournalSheet = ({ tab, draft, setDraft, ignited, onReset }) => {
  const isForgive = tab.key === 'self' || tab.key === 'others';
  const handleReset = onReset;

  if (ignited) {
    return (
      <div
        data-testid={`divine-affirmation-${tab.key}`}
        className="rounded-2xl p-6 flex flex-col items-center text-center relative overflow-hidden"
        style={{
          background: `linear-gradient(160deg, ${tab.color}25, ${GOLD}18 55%, ${GOLD}05 100%)`,
          border: `2px solid ${GOLD_BRIGHT}`,
          boxShadow: `0 0 34px ${GOLD}77, 0 0 68px ${tab.color}55, inset 0 0 24px ${GOLD}33`,
          minHeight: 320,
          animation: 'divineIgnite 800ms ease-out',
        }}>
        {/* Radiant sunburst */}
        <div className="absolute inset-0 pointer-events-none opacity-40"
          style={{ background: `radial-gradient(circle at 50% 30%, ${GOLD_BRIGHT}55, transparent 55%)` }}/>
        <Sparkles size={44}
          style={{ color: GOLD_BRIGHT,
            filter: `drop-shadow(0 0 18px ${GOLD})`,
            animation: 'divineSparkle 2.4s ease-in-out infinite' }}/>
        <p className="font-['Cinzel'] uppercase tracking-[0.28em] mt-4"
          style={{ color: GOLD_BRIGHT, fontSize: 13, textShadow: `0 0 14px ${GOLD}77` }}>
          {isForgive ? 'Energy · Released' : 'Energy · Amplified'}
        </p>
        <p className="font-['Outfit'] italic mt-3 mb-4 relative z-10"
          style={{ color: '#FFF6D0', fontSize: 15, lineHeight: 1.55, maxWidth: 320,
            textShadow: `0 0 8px ${GOLD}44` }}>
          {tab.affirmation}
        </p>
        <p className="font-['Outfit'] mt-1 relative z-10"
          style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, maxWidth: 320, lineHeight: 1.4 }}>
            Your words have been sealed and transmuted. The next sheet is fresh — write more, or move to the next tab.
        </p>
        <button type="button" onClick={handleReset}
          data-testid={`divine-affirmation-${tab.key}-edit`}
          className="mt-5 rounded-full px-4 py-1.5 font-['Outfit'] uppercase tracking-[0.22em] relative z-10"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${GOLD}66`,
            color: GOLD_BRIGHT, fontSize: 10,
          }}>
          Write more · Fresh Sheet
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <p className="font-['Cinzel'] uppercase tracking-[0.28em] mb-2"
        style={{ color: tab.color, fontSize: 13,
          textShadow: `0 0 10px ${tab.color}55` }}
        data-testid={`divine-tab-question-${tab.key}`}>
        {tab.question}
      </p>
      <p className="font-['Outfit'] italic mb-3"
        style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11.5, lineHeight: 1.45 }}>
        {tab.hint}
      </p>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, 3000))}
        placeholder={tab.placeholder}
        rows={12}
        data-testid={`divine-tab-textarea-${tab.key}`}
        className="w-full rounded-xl px-4 py-3 font-['Outfit'] resize-none"
        style={{
          background: `linear-gradient(180deg, rgba(20,15,10,0.7), rgba(5,3,8,0.9))`,
          border: `1.5px solid ${tab.color}66`,
          boxShadow: `inset 0 0 18px ${tab.color}22, 0 0 12px ${tab.color}18`,
          color: 'rgba(255,255,255,0.94)',
          fontSize: 14, lineHeight: 1.6, outline: 'none',
          minHeight: 240,
        }}/>
      <div className="flex items-center justify-between mt-1.5">
        <p className="font-['Outfit']"
          style={{ color: `${tab.color}88`, fontSize: 9 }}>
          {draft.length}/3000
        </p>
        <p className="font-['Outfit'] italic"
          style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
          Write freely. One line per thought if you prefer.
        </p>
      </div>
    </div>
  );
};

const Phase1 = ({ journal, onChange, onNext, onBack, onJumpPhase, communionFlicker }) => {
  const [activeTab, setActiveTab] = useState('self');
  const [ignitedTabs, setIgnitedTabs] = useState(new Set());
  const [drafts, setDrafts] = useState({});
  const [showAff, setShowAff] = useState({});
  const currentTab = P1_TABS.find(t => t.key === activeTab);
  const draft = drafts[activeTab] || '';
  const setDraft = (v) => setDrafts(prev => ({ ...prev, [activeTab]: v }));
  const canRelease = !!draft.trim() && !showAff[activeTab];
  const handleIgnite = () => {
    const newItems = draft.trim().split('\n').map(l => l.trim().slice(0, 500)).filter(Boolean);
    if (!newItems.length) return;
    const existing = journal[currentTab.field] || [];
    // append + trim total to 200 items so the doc never balloons
    const merged = [...existing, ...newItems].slice(-200);
    onChange({ ...journal, [currentTab.field]: merged });
    setIgnitedTabs(prev => new Set([...prev, activeTab]));
    setDrafts(prev => ({ ...prev, [activeTab]: '' }));
    setShowAff(prev => ({ ...prev, [activeTab]: true }));
    try { document.activeElement?.blur?.(); } catch (_) { /* noop */ }
    RELEASE_SFX[activeTab]?.();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="divine-phase1">
      <PhaseHeader phase={1} onBack={onBack} onJumpPhase={onJumpPhase}
        communionFlicker={communionFlicker}/>

      {/* 4-tab sub-nav */}
      <div className="flex-shrink-0 px-2 pt-2 pb-1.5 border-b"
        style={{ borderColor: `${GOLD}18` }}>
        <div className="flex gap-1 overflow-x-auto scrollbar-hide" data-testid="divine-phase1-tabs">
          {P1_TABS.map(t => {
            const active = t.key === activeTab;
            const isDone = ignitedTabs.has(t.key);
            return (
              <button key={t.key} type="button"
                onClick={() => setActiveTab(t.key)}
                data-testid={`divine-tab-${t.key}`}
                className="flex-1 min-w-[74px] rounded-lg py-1.5 px-1 font-['Cinzel'] uppercase tracking-[0.14em] transition-all flex flex-col items-center gap-0.5"
                style={{
                  background: active
                    ? `linear-gradient(180deg, ${t.color}dd, ${t.color}55)`
                    : 'rgba(255,255,255,0.03)',
                  color: active ? '#FFF' : `${t.color}dd`,
                  border: `1px solid ${t.color}${active ? 'ff' : '44'}`,
                  fontSize: 9,
                  boxShadow: active ? `0 0 12px ${t.color}88` : 'none',
                }}>
                <span>{t.short}</span>
                {isDone && <Check size={9} strokeWidth={3} style={{ color: active ? '#0a0a0a' : GOLD_BRIGHT }}/>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Journal sheet — key={activeTab} guarantees full remount + empty draft when tab changes */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-4 scrollbar-hide">
        <JournalSheet
          key={activeTab}
          tab={currentTab}
          draft={draft}
          setDraft={setDraft}
          ignited={!!showAff[activeTab]}
          onReset={() => setShowAff(prev => ({ ...prev, [activeTab]: false }))}/>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-3 pt-2 border-t"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)',
        }}>
        {canRelease ? (
          <button type="button" onClick={handleIgnite}
            onMouseDown={(e) => e.preventDefault()}
            data-testid={`divine-tab-ignite-${currentTab.key}`}
            className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em] flex items-center justify-center gap-2"
            style={{
              background: `linear-gradient(180deg, ${currentTab.color}, ${currentTab.color}66)`,
              color: '#FFF', border: `1.5px solid ${currentTab.color}`,
              fontSize: 12, boxShadow: `0 0 18px ${currentTab.color}88`,
            }}>
            <Sparkles size={14}/> {currentTab.ctaLabel}
          </button>
        ) : (
        <button type="button" onClick={onNext}
          data-testid="divine-phase1-next"
          className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em]"
          style={{
            background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
            color: '#0a0a0a', border: `1px solid ${GOLD}`,
            fontSize: 12, boxShadow: `0 0 16px ${GOLD}66`,
          }}>
          Advance → Phase 2 · Elevation
        </button>
        )}
      </div>

      {/* Ignite animation keyframes */}
      <style>{`
        @keyframes divineIgnite {
          0%   { opacity: 0; transform: scale(0.9); filter: blur(6px); }
          60%  { opacity: 1; transform: scale(1.03); filter: blur(0); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes divineSparkle {
          0%, 100% { transform: rotate(0deg) scale(1); opacity: 0.85; }
          50%      { transform: rotate(180deg) scale(1.15); opacity: 1; }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

/* ═════════ PHASE 2 — Divine Breath + Visualization ═════════ */
const Phase2 = ({ journal, onChange, onNext, onBack, onJumpPhase, communionFlicker }) => {
  const AIR = 30;   // seconds per air segment
  const VOID = 30;  // seconds per void segment
  const TOTAL_CYCLES = 20; // 20 × 30s = 600s = 10 min

  const [running, setRunning] = useState(false);
  const [tick, setTick] = useState({ phase: 'air', cycleIndex: 0, secondsIntoPhase: 0, done: false });
  const [elapsed, setElapsed] = useState(0);
  const sessionRef = useRef(null);
  const startTsRef = useRef(0);

  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
    }, 200);
    return () => clearInterval(iv);
  }, [running]);

  const beginTimerRef = useRef(null);
  useEffect(() => () => { clearTimeout(beginTimerRef.current); stopBreathSession(); stopSpeech(); }, []);

  const begin = () => {
    if (running) return;
    startChime();
    setTimeout(() => speak('Close your eyes. Listen to the air. Then visualize your desire in the silence.'), 400);
    startTsRef.current = Date.now() + 4000;  // spoken opener ≈ 4s
    beginTimerRef.current = setTimeout(() => {
      sessionRef.current = startBreathSession({
        airSec: AIR, voidSec: VOID, totalCycles: TOTAL_CYCLES,
        onTick: (t) => setTick(t),
        onComplete: () => {
          setRunning(false);
          endChime();
          setTimeout(() => speak('Elevation complete. Advance when ready.'), 700);
        },
      });
      setRunning(true);
      startTsRef.current = Date.now();
    }, 4000);
  };

  const stop = () => {
    clearTimeout(beginTimerRef.current);
    stopBreathSession();
    stopSpeech();
    setRunning(false);
    startChime();
  };

  const totalRemaining = Math.max(0, TOTAL_CYCLES * (AIR + VOID) - elapsed);
  const cycleProgress = tick.secondsIntoPhase / (tick.phase === 'air' ? AIR : VOID);

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="divine-phase2">
      <PhaseHeader phase={2} onBack={onBack} onJumpPhase={onJumpPhase}
        communionFlicker={communionFlicker}/>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-4">
        {!running ? (
          <>
            {/* Setup / Instructions */}
            <div className="rounded-xl p-4 mb-4"
              style={{
                background: `linear-gradient(135deg, ${AZURE}15, ${AZURE}05)`,
                border: `1.5px solid ${AZURE}77`,
                boxShadow: `0 0 16px ${AZURE}33`,
              }}
              data-testid="divine-phase2-instructions">
              <p className="font-['Cinzel'] uppercase tracking-[0.28em] mb-2"
                style={{ color: AZURE, fontSize: 11 }}>Divine Breath Protocol</p>
              <ul className="space-y-1.5 font-['Outfit']"
                style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, lineHeight: 1.5 }}>
                <li>• Sit in Easy Pose (Sukhasana), spine straight.</li>
                <li>• <strong>Close your eyes when session starts</strong> — keep them closed throughout.</li>
                <li>• <strong>AIR phase (30s):</strong> listen to the breath sound. Focus mind on the air.</li>
                <li>• <strong>VOID phase (30s):</strong> in the silence, visualize your desire as if it has already occurred.</li>
                <li>• 20 cycles · 10 minutes total.  Deep rhythmic inhale/exhale throughout.</li>
              </ul>
            </div>

            {/* Visualization prompt */}
            <label className="font-['Outfit'] uppercase tracking-[0.24em] mb-2 block"
              style={{ color: GOLD, fontSize: 10 }}>
              Describe your future as if it is happening now
            </label>
            <textarea
              value={journal.visualization}
              onChange={(e) => onChange({ ...journal, visualization: e.target.value.slice(0, 3000) })}
              placeholder="I am standing on the mountaintop. I have built..."
              rows={5}
              data-testid="divine-visualization-text"
              className="w-full rounded-lg px-3 py-2.5 font-['Outfit'] resize-none"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${AZURE}44`,
                color: 'rgba(255,255,255,0.9)',
                fontSize: 13, outline: 'none',
              }}/>
            <p className="text-right font-['Outfit'] mt-1"
              style={{ color: GOLD_SOFT, fontSize: 9 }}>{journal.visualization.length}/3000</p>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-4">
            {/* Big breathing orb — expands during air, contracts during void */}
            <div className="relative flex items-center justify-center mb-6"
              style={{ width: 260, height: 260 }}>
              <div
                data-testid="divine-breath-orb"
                style={{
                  width: tick.phase === 'air' ? 240 : 130,
                  height: tick.phase === 'air' ? 240 : 130,
                  borderRadius: '50%',
                  background: tick.phase === 'air'
                    ? `radial-gradient(circle at 40% 35%, ${AZURE}88, ${AZURE}22 50%, transparent 78%)`
                    : `radial-gradient(circle at 40% 35%, ${AMETHYST}66, ${AMETHYST}18 55%, transparent 78%)`,
                  border: `1.5px solid ${tick.phase === 'air' ? AZURE : AMETHYST}`,
                  boxShadow: `0 0 40px ${tick.phase === 'air' ? AZURE : AMETHYST}aa, inset 0 0 30px ${GOLD}44`,
                  transition: 'width 3s ease-in-out, height 3s ease-in-out, background 1s, border-color 1s',
                }}
              />
              {tick.phase === 'air'
                ? <Wind size={44} className="absolute"
                    style={{ color: '#E0F5FF', filter: `drop-shadow(0 0 10px ${AZURE}aa)` }}/>
                : <Eye size={40} className="absolute"
                    style={{ color: '#F5E5FF', filter: `drop-shadow(0 0 10px ${AMETHYST}aa)` }}/>}
            </div>

            <p className="font-['Cinzel'] tracking-[0.35em]"
              style={{ color: tick.phase === 'air' ? AZURE : AMETHYST, fontSize: 15,
                textShadow: `0 0 12px ${tick.phase === 'air' ? AZURE : AMETHYST}66` }}
              data-testid="divine-phase-label">
              {tick.phase === 'air' ? 'LISTEN' : 'VISUALIZE'}
            </p>
            <p className="font-['Outfit'] italic text-center mt-2 px-4"
              style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, maxWidth: 320 }}>
              {tick.phase === 'air'
                ? 'Focus on the sound of the breath. Eyes closed.'
                : 'See your dream complete. Eyes closed.'}
            </p>

            {/* Cycle-progress ring */}
            <div className="w-full max-w-[260px] h-1 rounded-full mt-6"
              style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(100, cycleProgress * 100)}%`,
                  background: `linear-gradient(90deg, ${tick.phase === 'air' ? AZURE : AMETHYST}, ${GOLD_BRIGHT})`,
                }}/>
            </div>
            <p className="font-['Outfit'] uppercase tracking-[0.24em] mt-2"
              style={{ color: GOLD_SOFT, fontSize: 9 }}>
              Cycle {tick.cycleIndex + 1} / {TOTAL_CYCLES}
            </p>
          </div>
        )}
      </div>

      {/* Footer — Begin / Stop + Timer */}
      <div className="flex-shrink-0 px-3 pt-2 border-t"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)',
        }}>
        {running && (
          <div className="flex items-baseline justify-center gap-3 mb-2">
            <p className="font-['Cinzel']"
              style={{ color: GOLD_BRIGHT, fontSize: 30, letterSpacing: '0.15em',
                textShadow: `0 0 18px ${GOLD}66` }}
              data-testid="divine-timer">
              {fmtTime(totalRemaining)}
            </p>
            <p className="font-['Outfit'] uppercase tracking-[0.24em]"
              style={{ color: GOLD_SOFT, fontSize: 9 }}>remaining</p>
          </div>
        )}
        {!running ? (
          <div className="flex gap-2">
            <button type="button" onClick={begin}
              data-testid="divine-breath-begin"
              className="flex-[2] rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.24em] flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(180deg, ${AZURE}, ${AZURE}66)`,
                color: '#001824', border: `1px solid ${AZURE}`,
                fontSize: 12, boxShadow: `0 0 16px ${AZURE}55`,
              }}>
              <Play size={14}/> Begin · 10 min
            </button>
            <button type="button" onClick={onNext}
              data-testid="divine-phase2-skip"
              className="flex-1 rounded-xl py-3 font-['Outfit'] uppercase tracking-[0.22em]"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.14)',
                color: 'rgba(255,255,255,0.55)', fontSize: 10,
              }}>Skip →</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button type="button" onClick={stop}
              data-testid="divine-breath-stop"
              className="flex-[2] rounded-xl py-3 font-['Outfit'] uppercase tracking-[0.24em] flex items-center justify-center gap-2"
              style={{
                background: 'rgba(200,80,80,0.1)',
                border: '1px solid rgba(216,121,121,0.55)',
                color: '#D87979', fontSize: 11,
              }}>
              <Square size={12}/> End Breath
            </button>
            <button type="button" onClick={onNext}
              data-testid="divine-phase2-next"
              className="flex-1 rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.22em]"
              style={{
                background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
                color: '#0a0a0a', border: `1px solid ${GOLD}`, fontSize: 10,
              }}>Advance →</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═════════ PHASE 3 — Worship + Decree ═════════ */
/* ═════════ WorshipCard — one card per note, with its own Fuse ritual ═════════

   State machine per note (per day, keyed by local date):
     • upcoming  — scheduled_time set, not yet arrived (seconds_since < 0)
     • flickering — inside 2-min window & not fused (needs_flicker=true)
     • fused      — worshipped_today=true → Locked until midnight (next day_key)
     • missed     — scheduled_time set, past 2-min window, never fused → Reopens tomorrow
     • anytime    — no scheduled_time — always available (never flickers, never missed)
   ─────────────────────────────────────────────────────────────────────── */
const WorshipCard = ({ note, statusEntry, onFuse }) => {
  const [expanded, setExpanded] = useState(false);
  const [ask, setAsk] = useState(note.decree || '');
  const [fusing, setFusing] = useState(false);
  const worshippedToday = !!statusEntry?.worshipped_today;
  const isFlickering = !!statusEntry?.needs_flicker && !worshippedToday;
  const scheduledTime = note.scheduled_time;
  const secondsSince = statusEntry?.seconds_since_scheduled;
  // Missed = had a schedule + window closed (>120s) + not fused
  const isMissed = !!scheduledTime && !worshippedToday && !isFlickering
    && typeof secondsSince === 'number' && secondsSince > 120;

  useEffect(() => { setAsk(note.decree || ''); }, [note.decree]);
  // Collapse the prayer panel if we somehow got fused mid-flow
  useEffect(() => { if (worshippedToday) setExpanded(false); }, [worshippedToday]);

  const handleFuse = async () => {
    if (fusing || worshippedToday) return;
    setFusing(true);
    await onFuse(note.note_id, ask.trim());
    setFusing(false);
  };

  return (
    <div
      data-testid={`divine-worship-card-${note.note_id}`}
      className={`rounded-xl overflow-hidden ${isFlickering ? 'divine-note-flicker' : ''}`}
      style={{
        background: worshippedToday
          ? `linear-gradient(135deg, ${GOLD}18, ${GOLD}05)`
          : isMissed
            ? 'linear-gradient(135deg, rgba(90,90,90,0.14), rgba(35,35,35,0.06))'
            : `linear-gradient(135deg, ${AMETHYST}18, ${AMETHYST}05)`,
        border: `1.5px solid ${
          isFlickering ? '#FFF6D0'
          : worshippedToday ? `${GOLD}aa`
          : isMissed ? 'rgba(200,200,200,0.35)'
          : `${AMETHYST}88`
        }`,
        boxShadow: isFlickering
          ? `0 0 24px ${GOLD_BRIGHT}88, 0 0 44px ${AMETHYST}66`
          : worshippedToday
            ? `0 0 14px ${GOLD}44`
            : isMissed
              ? 'none'
              : `0 0 10px ${AMETHYST}22`,
        opacity: isMissed ? 0.72 : 1,
      }}>
      {/* Worship title + time + status pill */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-1 gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-['Cinzel'] uppercase tracking-[0.22em] truncate"
              style={{ color: GOLD_BRIGHT, fontSize: 12 }}>
              {note.title || 'Untitled Worship'}
            </p>
            {scheduledTime && (
              <p className="font-['Outfit'] uppercase tracking-[0.24em] mt-0.5"
                style={{ color: `${AMETHYST}dd`, fontSize: 9 }}>
                <Clock size={9} className="inline mr-1" style={{ verticalAlign: -1 }}/>
                {scheduledTime}
              </p>
            )}
          </div>
          {worshippedToday && (
            <span className="rounded-full px-2 py-1 flex items-center gap-1 flex-shrink-0"
              style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}88` }}
              data-testid={`divine-fused-badge-${note.note_id}`}>
              <Check size={10} style={{ color: GOLD_BRIGHT }} strokeWidth={3}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]"
                style={{ color: GOLD_BRIGHT, fontSize: 8 }}>Fused</span>
            </span>
          )}
          {isMissed && (
            <span className="rounded-full px-2 py-1 flex items-center gap-1 flex-shrink-0"
              style={{ background: 'rgba(200,80,80,0.14)', border: '1px solid rgba(216,121,121,0.55)' }}
              data-testid={`divine-missed-badge-${note.note_id}`}>
              <X size={10} style={{ color: '#D87979' }} strokeWidth={3}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]"
                style={{ color: '#D87979', fontSize: 8 }}>Missed</span>
            </span>
          )}
        </div>

        {/* Worship body */}
        <p className="font-['Outfit'] whitespace-pre-wrap mt-2"
          style={{ color: 'rgba(255,255,255,0.92)', fontSize: 13, lineHeight: 1.55 }}
          data-testid={`divine-worship-content-${note.note_id}`}>
          {note.content}
        </p>

        {/* Expand → Decree/Ask (only when not yet fused today AND not missed) */}
        {!worshippedToday && !isMissed && !expanded && (
          <button type="button"
            onClick={() => setExpanded(true)}
            data-testid={`divine-enter-prayer-${note.note_id}`}
            className="w-full rounded-lg py-2 mt-3 font-['Cinzel'] uppercase tracking-[0.24em] flex items-center justify-center gap-2"
            style={{
              background: `linear-gradient(180deg, ${AMETHYST}cc, ${AMETHYST}55)`,
              color: '#FFF', border: `1px solid ${AMETHYST}`,
              fontSize: 10, boxShadow: `0 0 12px ${AMETHYST}55`,
            }}>
            <Sparkles size={12}/> Enter Prayer · Write Ask
          </button>
        )}

        {/* Locked / missed status caption */}
        {worshippedToday && (
          <p className="font-['Outfit'] italic text-center mt-3"
            style={{ color: `${GOLD_SOFT}`, fontSize: 10 }}
            data-testid={`divine-locked-caption-${note.note_id}`}>
            Locked · Reopens at midnight
          </p>
        )}
        {isMissed && (
          <p className="font-['Outfit'] italic text-center mt-3"
            style={{ color: 'rgba(216,121,121,0.85)', fontSize: 10 }}
            data-testid={`divine-missed-caption-${note.note_id}`}>
            Missed today · Reopens tomorrow at {scheduledTime}
          </p>
        )}
      </div>

      {/* Decree/Ask panel */}
      {expanded && !worshippedToday && !isMissed && (
        <div className="px-4 pb-4 border-t"
          style={{ borderColor: `${GOLD}33`, background: `linear-gradient(180deg, ${GOLD}08, transparent 50%)` }}>
          <p className="font-['Cinzel'] uppercase tracking-[0.28em] mt-3 mb-1"
            style={{ color: GOLD_BRIGHT, fontSize: 11 }}>
            The Decree · Your Ask
          </p>
          <p className="font-['Outfit'] italic mb-2"
            style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5 }}>
            After worship, state your ask as a command — not a plea. What is inevitable now?
          </p>
          <textarea
            value={ask}
            onChange={(e) => setAsk(e.target.value.slice(0, 3000))}
            placeholder="I command that..."
            rows={5}
            data-testid={`divine-ask-text-${note.note_id}`}
            className="w-full rounded-lg px-3 py-2.5 font-['Outfit'] resize-none"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${GOLD}55`,
              color: 'rgba(255,255,255,0.92)',
              fontSize: 13, outline: 'none',
              minHeight: 110,
            }}/>
          <p className="text-right font-['Outfit'] mt-0.5 mb-2"
            style={{ color: GOLD_SOFT, fontSize: 9 }}>{ask.length}/3000</p>
          <button type="button"
            onClick={handleFuse}
            disabled={fusing}
            data-testid={`divine-fuse-btn-${note.note_id}`}
            className="w-full rounded-xl py-2.5 font-['Cinzel'] uppercase tracking-[0.28em] flex items-center justify-center gap-2 disabled:opacity-50"
            style={{
              background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
              color: '#0a0a0a', border: `1px solid ${GOLD}`,
              fontSize: 11, boxShadow: `0 0 16px ${GOLD}77`,
            }}>
            <Check size={13}/> {fusing ? 'Fusing…' : 'Fuse · Complete'}
          </button>
        </div>
      )}
    </div>
  );
};

const Phase3 = ({ journal, onChange, worshipStatus, worshipNotes, onFuse, onFinish, onBack, onOpenSettings, onJumpPhase, communionFlicker }) => {
  // Map note_id → status entry for quick lookup
  const statusByNote = {};
  (worshipStatus?.notes || []).forEach(n => { statusByNote[n.note_id] = n; });

  const pendingCount = (worshipStatus?.notes || []).filter(n => n.needs_flicker && !n.worshipped_today).length;

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="divine-phase3">
      <PhaseHeader phase={3} onBack={onBack} onJumpPhase={onJumpPhase}
        communionFlicker={communionFlicker}/>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-4 space-y-4">

        {/* Worship section */}
        <section data-testid="divine-worship-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen size={14} style={{ color: AMETHYST }}/>
              <p className="font-['Cinzel'] uppercase tracking-[0.28em]"
                style={{ color: AMETHYST, fontSize: 11 }}>Worship · Prayer</p>
              {pendingCount > 0 && (
                <span className="rounded-full px-2 py-0.5 font-['Outfit'] uppercase tracking-[0.2em]"
                  style={{ background: `${GOLD_BRIGHT}22`, border: `1px solid ${GOLD_BRIGHT}`, color: GOLD_BRIGHT, fontSize: 8 }}
                  data-testid="divine-pending-count">
                  {pendingCount} pending
                </span>
              )}
            </div>
            <button type="button" onClick={onOpenSettings}
              data-testid="divine-worship-settings-btn"
              className="rounded-full px-2.5 py-1 font-['Outfit'] uppercase tracking-[0.2em]"
              style={{ background: 'rgba(139,63,191,0.1)', border: `1px solid ${AMETHYST}55`, color: AMETHYST, fontSize: 9 }}>
              <Plus size={10} className="inline mr-1"/> Manage
            </button>
          </div>

          {worshipNotes.length === 0 ? (
            <div className="rounded-xl p-4 text-center"
              style={{ background: `${AMETHYST}10`, border: `1px dashed ${AMETHYST}66` }}>
              <p className="font-['Outfit']"
                style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
                No worships yet. Tap <strong style={{ color: AMETHYST }}>Manage</strong> to add worship notes with their own scheduled times.
                When a time hits, the Energy tile will flicker for 2 minutes — read the worship, write your Ask (Decree), then Fuse.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="divine-worship-cards">
              {worshipNotes.map(n => (
                <WorshipCard
                  key={n.note_id}
                  note={n}
                  statusEntry={statusByNote[n.note_id]}
                  onFuse={onFuse}/>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-3 pt-2 border-t"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)',
        }}>
        <button type="button" onClick={onFinish}
          data-testid="divine-finish"
          className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.32em]"
          style={{
            background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
            color: '#0a0a0a', border: `1px solid ${GOLD}`,
            fontSize: 12, boxShadow: `0 0 18px ${GOLD}66`,
          }}>
          Fuse · Save Session
        </button>
      </div>

      <style>{`
        @keyframes divineNoteFlicker {
          0%, 100% {
            box-shadow: 0 0 12px rgba(139,63,191,0.28), inset 0 0 8px rgba(139,63,191,0.15);
            border-color: rgba(139,63,191,0.55);
          }
          50% {
            box-shadow: 0 0 26px rgba(255,246,208,0.75), 0 0 46px rgba(139,63,191,0.5), inset 0 0 18px rgba(255,246,208,0.28);
            border-color: #FFF6D0;
          }
        }
        .divine-note-flicker { animation: divineNoteFlicker 1.4s ease-in-out infinite; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

/* ═════════ Worship Settings modal (Holy Hour + note editor) ═════════ */
const WorshipSettings = ({ notes, onCreateNote, onUpdateNote, onDeleteNote, onClose }) => {
  const [editing, setEditing] = useState(null); // note | 'new' | null
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [draftDecree, setDraftDecree] = useState('');
  const [draftTime, setDraftTime] = useState('');

  const startNew = () => {
    setEditing('new');
    setDraftTitle(''); setDraftContent(''); setDraftDecree(''); setDraftTime('');
  };
  const startEdit = (n) => {
    setEditing(n);
    setDraftTitle(n.title || '');
    setDraftContent(n.content || '');
    setDraftDecree(n.decree || '');
    setDraftTime(n.scheduled_time || '');
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraftTitle(''); setDraftContent(''); setDraftDecree(''); setDraftTime('');
  };

  const saveDraft = async () => {
    if (!draftContent.trim()) return;
    const payload = {
      title: draftTitle.trim(),
      content: draftContent.trim(),
      decree: draftDecree.trim(),
      scheduled_time: draftTime.trim() || null,
      tz_offset_min: tzOffsetMin(),
    };
    if (editing === 'new') await onCreateNote(payload);
    else await onUpdateNote(editing.note_id, payload);
    cancelEdit();
  };

  return (
    <div className="fixed inset-0 z-[240] flex flex-col" style={{ background: BG }}
      data-testid="divine-worship-settings">
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2.5 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <button type="button" onClick={onClose}
          data-testid="divine-worship-settings-close"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}55`, color: GOLD }}>
          <ChevronLeft size={16}/>
          <span className="font-['Outfit'] text-[11px] uppercase tracking-[0.2em]">Back</span>
        </button>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: `0 0 10px ${GOLD}55` }}>
          WORSHIP · MANAGE
        </p>
        <span className="w-14"/>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-4 space-y-4"
        style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)' }}>
        <div className="rounded-lg p-3"
          style={{ background: `${GOLD}0A`, border: `1px solid ${GOLD}33` }}>
          <p className="font-['Outfit']"
            style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, lineHeight: 1.5 }}>
            <strong style={{ color: GOLD_BRIGHT }}>How it works.</strong> Each worship has its own scheduled time.
            When a time hits, the Energy tile flickers for exactly <strong>2 minutes</strong>.
            Open the modal → the Communion pill flickers → your worship card flickers →
            read the worship, write your Ask, tap <strong>Fuse · Complete</strong>. Miss the window? The flicker
            stops automatically — that worship is missed for the day.
          </p>
        </div>

        {/* Worship Notes */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen size={14} style={{ color: AMETHYST }}/>
              <p className="font-['Cinzel'] uppercase tracking-[0.28em]"
                style={{ color: AMETHYST, fontSize: 11 }}>My Worships</p>
            </div>
            {!editing && (
              <button type="button" onClick={startNew}
                data-testid="divine-new-note-btn"
                className="rounded-full px-3 py-1 font-['Outfit'] uppercase tracking-[0.2em] flex items-center gap-1"
                style={{
                  background: `linear-gradient(180deg, ${AMETHYST}, ${AMETHYST}55)`,
                  color: '#FFF', border: `1px solid ${AMETHYST}`, fontSize: 9,
                }}>
                <Plus size={11}/> Add
              </button>
            )}
          </div>

          {editing ? (
            <div className="rounded-xl p-3 space-y-2"
              style={{ background: `${AMETHYST}12`, border: `1px solid ${AMETHYST}66` }}
              data-testid="divine-note-editor">
              <input type="text" value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value.slice(0, 120))}
                placeholder="Worship name (e.g. Morning Praise · 5:30)"
                data-testid="divine-note-title-input"
                className="w-full rounded-lg px-3 py-2 font-['Outfit']"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${AMETHYST}55`,
                  color: 'rgba(255,255,255,0.9)', fontSize: 12, outline: 'none',
                }}/>

              {/* Scheduled time */}
              <div>
                <label className="font-['Outfit'] uppercase tracking-[0.22em] block mb-1"
                  style={{ color: GOLD, fontSize: 9 }}>
                  <Clock size={9} className="inline mr-1" style={{ verticalAlign: -1 }}/>
                  Scheduled time (blank = no alarm)
                </label>
                <input type="time" onClick={(e) => openPicker(e.currentTarget)} value={draftTime}
                  onChange={(e) => setDraftTime(e.target.value)}
                  data-testid="divine-note-time-input"
                  className="w-full rounded-lg px-3 py-2 font-['Outfit']"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${GOLD}55`,
                    color: 'rgba(255,255,255,0.9)', fontSize: 13, outline: 'none',
                  }}/>
              </div>

              {/* Worship body */}
              <div>
                <label className="font-['Outfit'] uppercase tracking-[0.22em] block mb-1 mt-1"
                  style={{ color: AMETHYST, fontSize: 9 }}>
                  <BookOpen size={9} className="inline mr-1" style={{ verticalAlign: -1 }}/>
                  Worship text
                </label>
                <textarea value={draftContent}
                  onChange={(e) => setDraftContent(e.target.value.slice(0, 5000))}
                  placeholder="Write your worship: praise, dedication, sacred intent..."
                  rows={6}
                  data-testid="divine-note-content-input"
                  className="w-full rounded-lg px-3 py-2 font-['Outfit'] resize-none"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${AMETHYST}55`,
                    color: 'rgba(255,255,255,0.9)', fontSize: 12, outline: 'none',
                  }}/>
              </div>

              {/* Decree (default ask) */}
              <div>
                <label className="font-['Outfit'] uppercase tracking-[0.22em] block mb-1 mt-1"
                  style={{ color: GOLD_BRIGHT, fontSize: 9 }}>
                  <Sparkles size={9} className="inline mr-1" style={{ verticalAlign: -1 }}/>
                  Default Ask · Decree (optional — pre-fills after worship)
                </label>
                <textarea value={draftDecree}
                  onChange={(e) => setDraftDecree(e.target.value.slice(0, 3000))}
                  placeholder="I command that... (pre-fill so you can just tap Fuse when the time hits)"
                  rows={4}
                  data-testid="divine-note-decree-input"
                  className="w-full rounded-lg px-3 py-2 font-['Outfit'] resize-none"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${GOLD}44`,
                    color: 'rgba(255,255,255,0.9)', fontSize: 12, outline: 'none',
                  }}/>
              </div>

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={cancelEdit}
                  data-testid="divine-note-cancel"
                  className="flex-1 rounded-lg py-2 font-['Outfit'] uppercase tracking-[0.22em]"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    color: 'rgba(255,255,255,0.55)', fontSize: 10,
                  }}>Cancel</button>
                <button type="button" onClick={saveDraft}
                  disabled={!draftContent.trim()}
                  data-testid="divine-note-save"
                  className="flex-[2] rounded-lg py-2 font-['Cinzel'] uppercase tracking-[0.26em] disabled:opacity-40"
                  style={{
                    background: `linear-gradient(180deg, ${AMETHYST}, ${AMETHYST}66)`,
                    color: '#FFF', border: `1px solid ${AMETHYST}`, fontSize: 10,
                  }}>Save Worship</button>
              </div>
            </div>
          ) : notes.length === 0 ? (
            <p className="font-['Outfit'] italic text-center py-3"
              style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
              (No worships yet — tap &ldquo;+ Add&rdquo; to create your first)
            </p>
          ) : (
            <div className="space-y-2" data-testid="divine-notes-list">
              {notes.map(n => (
                <div key={n.note_id} className="rounded-lg p-3"
                  style={{ background: `${AMETHYST}10`, border: `1px solid ${AMETHYST}44` }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <p className="font-['Cinzel'] uppercase tracking-[0.2em] truncate"
                        style={{ color: GOLD_BRIGHT, fontSize: 11 }}>
                        {n.title || 'Untitled'}
                      </p>
                      {n.scheduled_time && (
                        <p className="font-['Outfit'] uppercase tracking-[0.24em] mt-0.5"
                          style={{ color: GOLD, fontSize: 9 }}>
                          <Clock size={9} className="inline mr-1" style={{ verticalAlign: -1 }}/>
                          Fires at {n.scheduled_time}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => startEdit(n)}
                        data-testid={`divine-note-edit-${n.note_id}`}
                        className="rounded p-1 opacity-70 hover:opacity-100"
                        style={{ color: AMETHYST }}>
                        <BookOpen size={12}/>
                      </button>
                      <button type="button"
                        onClick={() => { if (window.confirm('Delete this worship?')) onDeleteNote(n.note_id); }}
                        data-testid={`divine-note-delete-${n.note_id}`}
                        className="rounded p-1 opacity-70 hover:opacity-100"
                        style={{ color: CRIMSON }}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </div>
                  <p className="font-['Outfit'] whitespace-pre-wrap"
                    style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, lineHeight: 1.4 }}>
                    {(n.content || '').slice(0, 180)}{(n.content || '').length > 180 ? '…' : ''}
                  </p>
                  {n.decree && (
                    <p className="font-['Outfit'] italic mt-1"
                      style={{ color: `${GOLD}bb`, fontSize: 10.5 }}>
                      Decree: {(n.decree || '').slice(0, 90)}{(n.decree || '').length > 90 ? '…' : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

/* ═════════ MAIN — Divine Energy modal ═════════ */
export const TheDivine = ({ onClose }) => {
  useHistoryClose(true, onClose);
  const [phase, setPhase] = useState(1);
  const [journal, setJournal] = useState({
    forgiveness: [], forgiveness_others: [], gratitude_have: [], gratitude_will: [],
    visualization: '', decree: '',
  });
  const [profile, setProfile] = useState({ holy_hour: null, tz_offset_min: 0 });
  const [notes, setNotes] = useState([]);
  const [worshipStatus, setWorshipStatus] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const startTsRef = useRef(Date.now());
  const notesLoadedRef = useRef(false);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [j, p, n, ws] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/divine/journal?date_key=${todayKey()}`),
          axios.get(`${BACKEND_URL}/api/divine/profile`),
          axios.get(`${BACKEND_URL}/api/divine/worship-notes`),
          axios.get(`${BACKEND_URL}/api/divine/worship-status`),
        ]);
        if (cancelled) return;
        setJournal({
          forgiveness: j.data.forgiveness || [],
          forgiveness_others: j.data.forgiveness_others || [],
          gratitude_have: j.data.gratitude_have || [],
          gratitude_will: j.data.gratitude_will || [],
          visualization: j.data.visualization || '',
          decree: j.data.decree || '',
        });
        setProfile(p.data);
        notesLoadedRef.current = true;
        setNotes(n.data || []);
        setWorshipStatus(ws.data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep Aura Codex energy alarms mirrored to the worship schedule.
  useEffect(() => {
    if (!notesLoadedRef.current) return;
    pushNotesToCodex(notes);
  }, [notes]);

  // Auto-save journal on blur / phase change (debounced)
  const journalDirtyRef = useRef(false);
  useEffect(() => { journalDirtyRef.current = true; }, [journal]);
  const saveJournal = async () => {
    if (!journalDirtyRef.current) return;
    try {
      await axios.post(`${BACKEND_URL}/api/divine/journal`, journal);
      journalDirtyRef.current = false;
    } catch { /* noop */ }
  };

  const handleNext = async (nextPhase) => {
    await saveJournal();
    setPhase(nextPhase);
  };

  const handleFinish = async () => {
    await saveJournal();
    try {
      await axios.post(`${BACKEND_URL}/api/divine/session`, {
        phase1_done: true, phase2_done: true, phase3_done: true,
        duration_sec: Math.round((Date.now() - startTsRef.current) / 1000),
      });
    } catch { /* noop */ }
    pingAuraFuse('energy');
    onClose();
  };

  const handleFuse = async (noteId, ask) => {
    try {
      await axios.post(`${BACKEND_URL}/api/divine/worship-complete`,
        { note_id_used: noteId, ask });
      const ws = await axios.get(`${BACKEND_URL}/api/divine/worship-status`);
      setWorshipStatus(ws.data);
      // Refresh tile blink hook
      window.dispatchEvent(new CustomEvent('divine:worship-completed'));
    } catch { /* noop */ }
    pingAuraFuse('energy');
  };

  const handleCreateNote = async (payload) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/divine/worship-notes`, payload);
      setNotes(prev => [data, ...prev]);
      // If a scheduled_time was set, refresh worship status so the flicker chain can re-evaluate
      if (payload?.scheduled_time) {
        const ws = await axios.get(`${BACKEND_URL}/api/divine/worship-status`);
        setWorshipStatus(ws.data);
        window.dispatchEvent(new CustomEvent('divine:profile-updated'));
      }
    } catch { /* noop */ }
  };
  const handleUpdateNote = async (note_id, patch) => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/divine/worship-notes/${note_id}`, patch);
      setNotes(prev => prev.map(n => n.note_id === note_id ? data : n));
      if ('scheduled_time' in (patch || {})) {
        const ws = await axios.get(`${BACKEND_URL}/api/divine/worship-status`);
        setWorshipStatus(ws.data);
        window.dispatchEvent(new CustomEvent('divine:profile-updated'));
      }
    } catch { /* noop */ }
  };
  const handleDeleteNote = async (note_id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/divine/worship-notes/${note_id}`);
      setNotes(prev => prev.filter(n => n.note_id !== note_id));
      const ws = await axios.get(`${BACKEND_URL}/api/divine/worship-status`);
      setWorshipStatus(ws.data);
      window.dispatchEvent(new CustomEvent('divine:profile-updated'));
    } catch { /* noop */ }
  };

  // Communion pill should flicker when any worship note is currently flickering
  const communionFlicker = (worshipStatus?.notes || []).some(n => n.needs_flicker && !n.worshipped_today);

  return (
    <div className="fixed inset-0 z-[220] flex flex-col"
      style={{ background: BG }} data-testid="divine-modal">
      {phase === 1 && (
        <Phase1 journal={journal} onChange={setJournal}
          onNext={() => handleNext(2)} onBack={onClose}
          onJumpPhase={handleNext}
          communionFlicker={communionFlicker}/>
      )}
      {phase === 2 && (
        <Phase2 journal={journal} onChange={setJournal}
          onNext={() => handleNext(3)} onBack={() => handleNext(1)}
          onJumpPhase={handleNext}
          communionFlicker={communionFlicker}/>
      )}
      {phase === 3 && (
        <Phase3 journal={journal} onChange={setJournal}
          worshipStatus={worshipStatus} worshipNotes={notes}
          onFuse={handleFuse}
          onFinish={handleFinish}
          onBack={() => handleNext(2)}
          onJumpPhase={handleNext}
          communionFlicker={communionFlicker}
          onOpenSettings={() => setSettingsOpen(true)}/>
      )}
      {settingsOpen && (
        <WorshipSettings notes={notes}
          onCreateNote={handleCreateNote}
          onUpdateNote={handleUpdateNote}
          onDeleteNote={handleDeleteNote}
          onClose={() => setSettingsOpen(false)}/>
      )}
    </div>
  );
};

/* Energy tile icon — radiant chalice / eternal flame */
export const DivineTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(233,199,122,0.75))' }}>
    <defs>
      <linearGradient id="divIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#FFF6D0"/>
        <stop offset="0.5" stopColor="#E9C77A"/>
        <stop offset="1" stopColor="#7A5722"/>
      </linearGradient>
      <radialGradient id="divCore" cx="50%" cy="45%" r="55%">
        <stop offset="0" stopColor="#FFF6D0"/>
        <stop offset="0.55" stopColor="#E9C77A"/>
        <stop offset="1" stopColor="#8B3FBF"/>
      </radialGradient>
    </defs>
    {/* Outer aura */}
    <circle cx="12" cy="12" r="10.5" fill="none"
      stroke="url(#divIcon)" strokeWidth="0.6" opacity="0.5"/>
    {/* Flame */}
    <path d="M12 3.2 C 15 6, 16.5 8.5, 16.5 11.5 C 16.5 14.5, 14.5 17, 12 17 C 9.5 17, 7.5 14.5, 7.5 11.5 C 7.5 8.5, 9 6, 12 3.2 Z"
      fill="url(#divCore)" stroke="url(#divIcon)" strokeWidth="0.8"/>
    {/* Chalice base */}
    <path d="M8.5 17 L 15.5 17 L 14.5 20 L 9.5 20 Z"
      fill="none" stroke="url(#divIcon)" strokeWidth="1.1"/>
    <line x1="7" y1="20.5" x2="17" y2="20.5"
      stroke="url(#divIcon)" strokeWidth="1.2" strokeLinecap="round"/>
    {/* Inner spark */}
    <circle cx="12" cy="12" r="1.4" fill="#FFF6D0"/>
  </svg>
);

export default TheDivine;
